var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { clamp, isFunction, isNil } from '../_internal/data';
import { stringifyColor } from '../color';
import { Text } from '../Text';
import { useThemeVariant } from '../useThemeVariant';
import { withLabel } from '../withLabel';
import { SliderContainer, SliderContent, SliderMainBar, SliderTooltips, SliderBackgroundDot, SliderIndicator, } from './Slider.style';
import { SliderBar } from './SliderBar';
import { SliderDot } from './SliderDot';
var InnerSlider = React.forwardRef(function (props, ref) {
    var _a = props.disabled, disabled = _a === void 0 ? false : _a, _b = props.shouldTooltipFollowDot, shouldTooltipFollowDot = _b === void 0 ? true : _b, _c = props.range, range = _c === void 0 ? false : _c, _d = props.onChange, onChange = _d === void 0 ? function () { } : _d, tooltipFormatter = props.tooltipFormatter, customValues = props.customValues, _e = props.indicators, rawIndicators = _e === void 0 ? [] : _e, _f = props.min, min = _f === void 0 ? 0 : _f, _g = props.max, rawMax = _g === void 0 ? 100 : _g, _h = props.step, rawStep = _h === void 0 ? 5 : _h, rawDots = props.dots, rawValue = props.value, _j = props.reversed, reversed = _j === void 0 ? false : _j, _k = props.dotType, dotType = _k === void 0 ? 'tag' : _k, rest = __rest(props, ["disabled", "shouldTooltipFollowDot", "range", "onChange", "tooltipFormatter", "customValues", "indicators", "min", "max", "step", "dots", "value", "reversed", "dotType"]);
    var max = customValues ? customValues.length : rawMax;
    var step = customValues ? 1 : rawStep;
    var dots = customValues ? true : rawDots;
    var emptyRange = React.useMemo(function () { return [min, max]; }, []);
    var value = !Array.isArray(rawValue) && range ? emptyRange : rawValue;
    var barRef = React.useRef(null);
    var _l = React.useState(value), localValue = _l[0], setLocalValue = _l[1];
    var localValueRef = React.useRef(value);
    var theme = useThemeVariant();
    var hasValue = range
        ? !isNil(localValue[0]) &&
            !isNil(localValue[1])
        : !isNil(localValue);
    var getPositionFromValue = React.useCallback(function (currentValue) {
        if (isNil(currentValue)) {
            return 0;
        }
        return (100 * (clamp(currentValue, min, max) - min)) / (max - min);
    }, [max, min]);
    var tooltips = React.useMemo(function () {
        var buildTooltip = function (tooltipValue) {
            var label = customValues
                ? customValues[tooltipValue - 1]
                : tooltipValue;
            var raw = "".concat(isNil(label) ? '' : label);
            var content = isFunction(tooltipFormatter)
                ? tooltipFormatter(tooltipValue, raw)
                : raw;
            return {
                content: content,
                position: getPositionFromValue(tooltipValue),
            };
        };
        if (range && Array.isArray(localValue)) {
            var rangeLocalValue = isNil(localValue[0]) || isNil(localValue[1])
                ? [min, max]
                : localValue;
            return [
                buildTooltip(rangeLocalValue[0]),
                buildTooltip(rangeLocalValue[1]),
            ];
        }
        return [
            buildTooltip(!localValue || isNil(localValue) ? min : localValue),
        ];
    }, [
        customValues,
        getPositionFromValue,
        localValue,
        range,
        tooltipFormatter,
        min,
        max,
    ]);
    var indicators = React.useMemo(function () {
        return rawIndicators.map(function (indicator) {
            var indicatorMinInRange = Math.min.apply(Math, indicator.range);
            var indicatorMaxInRange = Math.max.apply(Math, indicator.range);
            var indicatorMin = indicatorMinInRange > min ? indicatorMinInRange : min;
            var indicatorMax = indicatorMaxInRange < max ? indicatorMaxInRange : max;
            return __assign(__assign({ color: stringifyColor(theme.colors.error.base) }, indicator), { range: [
                    getPositionFromValue(indicatorMin),
                    getPositionFromValue(indicatorMax),
                ] });
        });
    }, [getPositionFromValue, max, min, rawIndicators, theme.colors.error.base]);
    React.useEffect(function () {
        localValueRef.current = localValue;
    }, [localValue]);
    React.useEffect(function () {
        setLocalValue(value);
    }, [value]);
    var getValueFromPosition = function (currentPosition) {
        var boundedPosition = Math.min(Math.max(currentPosition, 0), 100);
        var exactValue = (boundedPosition * (max - min)) / 100 + min;
        return Math.round(exactValue / step) * step;
    };
    var buildDot = function (rangeIndex) {
        if (rangeIndex === void 0) { rangeIndex = 0; }
        var getDotValue = function () {
            if (range) {
                if (rangeIndex === 1) {
                    return isNil(localValue[1])
                        ? max
                        : localValue[1];
                }
                return isNil(localValue[0])
                    ? min
                    : localValue[0];
            }
            return isNil(localValue) ? min : localValue;
        };
        var dotValue = getDotValue();
        var matchingIndicator = indicators.find(function (_a) {
            var indicatorRange = _a.range;
            return Math.min.apply(Math, indicatorRange) <= dotValue &&
                Math.max.apply(Math, indicatorRange) >= dotValue;
        });
        var position = getPositionFromValue(dotValue);
        var handlePositionChange = function (delta) {
            var newPosition = position +
                (delta /
                    (barRef.current
                        ? barRef.current.offsetWidth
                        : 0)) *
                    100;
            var newValue = getValueFromPosition(newPosition);
            setLocalValue(function (prev) {
                if (!range) {
                    return newValue;
                }
                var prevValue = isNil(prev)
                    ? [null, null]
                    : prev;
                if (rangeIndex === 0) {
                    return [newValue, isNil(prevValue[1]) ? max : prevValue[1]];
                }
                return [isNil(prevValue[0]) ? min : prevValue[0], newValue];
            });
        };
        return (React.createElement(SliderDot, { key: rangeIndex, position: position, onMove: handlePositionChange, onRest: function () { return handleChange(localValueRef.current); }, innerColor: matchingIndicator ? matchingIndicator.color : undefined, large: rangeIndex > 0, dotType: dotType, tooltip: tooltips[rangeIndex] }));
    };
    var handleBarClick = function (e) {
        var setValue = function (val) {
            setLocalValue(val);
            handleChange(val);
        };
        if (barRef.current) {
            var eventPosition = e.pageX - barRef.current.getBoundingClientRect().left;
            var newPosition = (eventPosition / barRef.current.offsetWidth) * 100;
            var positionValue = getValueFromPosition(newPosition);
            if (range) {
                if (!hasValue) {
                    setValue([min, positionValue]);
                }
                else {
                    var typedValue = localValue;
                    var isUpdatingFirstElement = Math.abs(positionValue - (typedValue[0] || 0)) <
                        Math.abs(positionValue - (typedValue[1] || 0));
                    setValue(isUpdatingFirstElement
                        ? [positionValue, typedValue[1]]
                        : [typedValue[0], positionValue]);
                }
            }
            else {
                setValue(positionValue);
            }
        }
    };
    var handleChange = function (val) {
        var sanitizedValue = Array.isArray(val) &&
            val[0] !== null &&
            val[1] !== null &&
            val[0] > val[1]
            ? [val[1], val[0]]
            : val;
        onChange(sanitizedValue);
    };
    var valueDots = range ? [buildDot(0), buildDot(1)] : buildDot();
    var valueBar = React.useMemo(function () {
        var getComponent = function (_a) {
            var from = _a.from, to = _a.to;
            return (React.createElement(SliderBar, { from: getPositionFromValue(from), to: getPositionFromValue(to), indicators: indicators }));
        };
        if (range) {
            if (!hasValue ||
                (Array.isArray(customValues) && customValues.length === 0)) {
                return null;
            }
            var rangeValue = localValue;
            return getComponent({
                from: Math.min.apply(Math, rangeValue),
                to: Math.max.apply(Math, rangeValue),
            });
        }
        return reversed
            ? getComponent({
                from: (hasValue ? localValue : min),
                to: max,
            })
            : getComponent({
                from: min,
                to: (hasValue ? localValue : min),
            });
    }, [
        customValues,
        getPositionFromValue,
        hasValue,
        indicators,
        localValue,
        min,
        range,
    ]);
    var valueIndicator = React.useMemo(function () {
        return indicators.map(function (indicator) {
            var getPosition = function (_a, opacity) {
                var from = _a[0], to = _a[1];
                if (opacity === void 0) { opacity = 1; }
                return ({
                    left: "".concat(from, "%"),
                    right: "".concat(100 - to, "%"),
                    opacity: opacity,
                });
            };
            return (React.createElement(React.Fragment, { key: indicator.range.join('.') },
                React.createElement(SliderIndicator, { color: "#fff", style: getPosition(indicator.range) }),
                React.createElement(SliderIndicator, { color: indicator.color, style: getPosition(indicator.range, 0.5) })));
        });
    }, [indicators]);
    var possibleValues = Array.from({ length: (max - min) / step + 1 }, function (_, i) { return (min + i) * step; });
    return (React.createElement(SliderContainer, { "data-dotType": dotType },
        React.createElement(SliderContent, __assign({}, rest, { "data-disabled": disabled, "data-has-value": hasValue, onClick: handleBarClick, ref: ref }),
            React.createElement(SliderMainBar, { ref: barRef }),
            valueDots,
            valueBar,
            valueIndicator,
            dots &&
                possibleValues.map(function (possibleValue, index) { return (React.createElement(SliderBackgroundDot, { key: index, style: { left: "".concat(getPositionFromValue(possibleValue), "%") } })); })),
        dotType === 'regular' && (React.createElement(SliderTooltips, { "data-fixed": !shouldTooltipFollowDot }, tooltips.map(function (tooltip, index) { return (React.createElement(Text, { key: index, "data-testid": "slider-tooltip", style: shouldTooltipFollowDot
                ? {
                    paddingLeft: "".concat(tooltip.position, "%"),
                    top: 0,
                    position: 'absolute',
                }
                : undefined, variation: "title" }, tooltip.content)); })))));
});
export var Slider = withLabel({
    orientation: 'vertical',
})(InnerSlider);
//# sourceMappingURL=Slider.js.map